/**
 * The zoom that puts a whole layout on screen, measured over its actual points.
 *
 * A sibling of `fitZoom` rather than a call to it, and the difference is the thing being fitted.
 * `fitZoom` takes a box and projects its eight corners, which is exactly right for the surface
 * charts - the axis box really is a box, and its corners really are drawn. A network is a point
 * cloud, and the corners of its bounding box are empty: a force layout settles into a ball, so the
 * furthest corner sits a factor of root three beyond any node. Fitting to it zoomed every view out
 * by that factor and left the graph a third of the size of its pane.
 *
 * What is reused is the measurement, which is the part worth not getting wrong twice: project at
 * zoom 1, take the largest offset from the center on each axis, and scale. That is exact rather
 * than approximate, because `zoom` enters `createProjector` only through `pixelsPerUnit` and the
 * foreshortening divisor depends on depth alone - so every screen offset is linear in zoom.
 *
 * Perspective is why this is needed at all. Near geometry is magnified by up to
 * `1 / (1 - perspective * extent)` - about 2.4x at the network camera's defaults - so a layout
 * that legitimately fills the unit cube projects well outside the viewport.
 */

import type { FitInset } from '../../geometry/fitZoom';
import { createProjector } from '../../geometry/projection';
import type { Size, ViewCamera } from '../../types';
import type { NetworkLayout, Vec3 } from '../types';

/**
 * Pixels to leave clear around the layout.
 *
 * Both cover the largest node's own radius, which the fit does not know about - it measures
 * centers. Vertical is the larger because a graph is wider than it is tall once fitted, so the
 * same margin in pixels is a smaller fraction of the height and a mark at the top or bottom edge
 * is the one that clips first.
 */
export const NETWORK_FIT_INSET: FitInset = {
  horizontal: 18,
  vertical: 34,
};

export type FitNetworkZoomArgs = {
  camera: ViewCamera;
  /** In CSS pixels, since the inset is in CSS pixels. */
  viewport: Size;
  layout: NetworkLayout;
  inset: FitInset;
};

export const fitNetworkZoom = ({
  camera,
  viewport,
  layout,
  inset,
}: FitNetworkZoomArgs): number => {
  const availableX = viewport.width / 2 - inset.horizontal;
  const availableY = viewport.height / 2 - inset.vertical;
  if (availableX <= 0 || availableY <= 0) return 1;

  /* Nodes, guides and the corners of every region - the same set `toUnitCube` bounds against, and
     for the same reason: a spline stays inside the hull of its endpoints, and a route that did
     escape should be clipped rather than allowed to shrink the whole graph.

     A mark is expanded by its own reserved radius where the layout reserved one, which is the
     grouped layout and only it - see `NetworkLayout.nodeRadii`. Everywhere else a mark is a few
     pixels across and the inset covers it, which is what the inset is for; there a mark is as
     large as its packing made it, and collapsing a grouping leaves a handful of marks each a
     tenth of the cube wide. Fitted to their centres they were cut off by the top of the pane. */
  const points: Vec3[] = [
    ...layout.positions.flatMap(([x, y, z], index): Vec3[] => {
      const radius = layout.nodeRadii?.[index] ?? 0;
      return radius <= 0
        ? [[x, y, z]]
        : [
            [x - radius, y - radius, z - radius],
            [x + radius, y + radius, z + radius],
          ];
    }),
    ...layout.guides.flat(),
    ...layout.volumes.flatMap(({ center: [x, y, z], radius }): Vec3[] => [
      [x - radius, y - radius, z - radius],
      [x + radius, y + radius, z + radius],
    ]),
  ];
  if (points.length === 0) return 1;

  const project = createProjector({ ...camera, zoom: 1 }, viewport);
  const centerX = viewport.width / 2;
  const centerY = viewport.height / 2;

  let reachX = 0;
  let reachY = 0;

  for (const [x, y, z] of points) {
    const { screenX, screenY } = project(x, y, z);
    reachX = Math.max(reachX, Math.abs(screenX - centerX));
    reachY = Math.max(reachY, Math.abs(screenY - centerY));
  }

  /* A layout that projects to a single point has nothing to fit against. */
  if (reachX <= 0 || reachY <= 0) return 1;

  return Math.min(availableX / reachX, availableY / reachY);
};
